import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaEnvelope, FaPhoneAlt, FaRegClock, FaShieldAlt, FaUserCircle } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';
import { MdCloudUpload, MdOutlineSecurity, MdSpaceDashboard } from 'react-icons/md';
import { FiActivity } from 'react-icons/fi';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { getSingleDataFromDatabase, userUploadProfilePicAPI } from '../utils/api';
import { BASEURLS } from '../utils/BaseUrl';

const tipItems = [
    {
        icon: <FaCheckCircle />,
        title: 'Keep your mobile number updated for OTP login.',
        accent: 'teal',
    },
    {
        icon: <MdOutlineSecurity />,
        title: 'Your report access is controlled by assigned PPC prefixes.',
        accent: 'green',
    },
    {
        icon: <FaShieldAlt />,
        title: 'Never share your OTP or login details with anyone.',
        accent: 'blue',
    },
    {
        icon: <HiSparkles />,
        title: 'Use refresh or filters when you need the latest live report data.',
        accent: 'gold',
    },
];

const MyProfile = () => {
    const ppcid = localStorage.getItem('ppcdata');
    const [ppc, setPPC] = useState([]);
    const [isLoading, setIsloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (ppcid) {
            getDashboardSingleEmpData(ppcid);
        } else {
            navigate('/');
        }
    }, [ppcid, navigate]);

    const getDashboardSingleEmpData = (id) => {
        setIsloading(true);
        getSingleDataFromDatabase(`ppc-get-single-data/${id}`).then((res) => {
            if (res.data.status === 1) {
                setPPC(...[res.data.ppcdata]);
                setIsloading(false);
            } else {
                setIsloading(false);
                toast.error('Check your connection');
            }
        });
    };

    const handleProfileUpload = (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile || !ppcid) return;

        const data = new FormData();
        data.append('ppc_foto', selectedFile);
        setIsUploading(true);

        userUploadProfilePicAPI(`ppc-update-profile-picture-data/${ppcid}`, data).then((res) => {
            setIsUploading(false);
            if (res.data.status === 1) {
                toast.success('Profile picture updated successfully');
                getDashboardSingleEmpData(ppcid);
            } else {
                toast.error('Unable to update profile picture');
            }
            event.target.value = '';
        }).catch(() => {
            setIsUploading(false);
            event.target.value = '';
            toast.error('Connection failed');
        });
    };

    const profileRows = [
        {
            icon: <FaUserCircle />,
            label: 'Full Name',
            value: ppc?.ppc_name || 'PPC User',
            accent: 'teal',
        },
        {
            icon: <FaPhoneAlt />,
            label: 'Mobile Number',
            value: ppc?.ppc_mobile || 'Not Available',
            accent: 'green',
        },
        {
            icon: <FaEnvelope />,
            label: 'Email Address',
            value: ppc?.ppc_email || 'Not Available',
            accent: 'purple',
        },
        {
            icon: <FaRegClock />,
            label: 'Last Login',
            value: ppc?.ppc_last_login || 'Not Available',
            accent: 'gold',
        },
    ];

    const accountRows = [
        { label: 'Account Type', value: 'PPC Team' },
        { label: 'Account Status', value: ppc?.ppc_status || 'Active' },
        { label: 'PPC ID', value: ppc?.ppc_id ? `#${ppc.ppc_id}` : 'N/A' },
        { label: 'Team Access', value: ppc?.ppc_team === '1' || ppc?.ppc_team === 1 ? 'Enabled' : 'Limited' },
        { label: 'Report Access', value: ppc?.ppc_report === '1' || ppc?.ppc_report === 1 ? 'Enabled' : 'Limited' },
        { label: 'Platform', value: 'Sat kartar Life Limited' },
    ];

    const profileImage = ppc?.ppc_foto ? BASEURLS(ppc.ppc_foto) : BASEURLS('user.png');

    return (
        <div className="ppc-profile-page">
            <Helmet>
                <title>SATKARTAR:PPC::MY PROFILE</title>
            </Helmet>

            <style>{`
                .ppc-profile-page .page-content {
                    background:
                        radial-gradient(circle at top left, rgba(64, 139, 191, 0.14), transparent 28%),
                        radial-gradient(circle at top right, rgba(31, 122, 63, 0.12), transparent 22%),
                        linear-gradient(180deg, #fffaf4 0%, #f5f8fc 45%, #eef3f7 100%);
                    min-height: calc(100vh - 65px);
                    padding: 1rem 1rem 110px;
                }
                .ppc-profile-shell {
                    display: grid;
                    grid-template-columns: minmax(320px, 460px) minmax(0, 1fr);
                    gap: 22px;
                    align-items: start;
                }
                .ppc-profile-card {
                    background: rgba(255, 255, 255, 0.94);
                    border-radius: 24px;
                    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
                    border: none;
                    overflow: hidden;
                }
                .ppc-profile-panel {
                    min-height: 100%;
                    position: relative;
                }
                .ppc-profile-hero {
                    height: 110px;
                    background: linear-gradient(135deg, #4d978f 0%, #4b8bbf 100%);
                    position: relative;
                    z-index: 1;
                    display: block;
                }
                .ppc-profile-body {
                    padding: 70px 26px 28px;
                    margin-top: 0;
                }
                .ppc-profile-avatar-row {
                    position: absolute;
                    top: 56px;
                    left: 26px;
                    right: 26px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    z-index: 4;
                }
                .ppc-profile-avatar {
                    width: 106px;
                    height: 106px;
                    border-radius: 50%;
                    border: 5px solid #fff;
                    background: #fff;
                    object-fit: cover;
                    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14);
                    position: relative;
                }
                .ppc-profile-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 14px;
                    border-radius: 999px;
                    background: #e8f8ec;
                    color: #22863a;
                    font-size: 12px;
                    font-weight: 700;
                }
                .ppc-profile-name {
                    margin-top: 0;
                    color: #0f172a;
                    font-size: 22px;
                    font-weight: 800;
                    line-height: 1.2;
                }
                .ppc-profile-subtitle {
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 600;
                    margin-top: 6px;
                }
                .ppc-profile-divider {
                    height: 1px;
                    background: linear-gradient(90deg, rgba(148,163,184,0.55), rgba(148,163,184,0.12));
                    margin: 22px 0 10px;
                }
                .ppc-profile-section-label,
                .ppc-profile-mini-label {
                    color: #94a3b8;
                    font-size: 12px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    text-transform: uppercase;
                }
                .ppc-profile-info-list {
                    margin-top: 10px;
                }
                .ppc-profile-info-item {
                    display: grid;
                    grid-template-columns: 40px minmax(0, 1fr);
                    gap: 14px;
                    align-items: center;
                    padding: 14px 0;
                    border-bottom: 1px solid #edf2f7;
                }
                .ppc-profile-info-item:last-child {
                    border-bottom: none;
                }
                .ppc-profile-info-icon,
                .ppc-profile-tip-icon,
                .ppc-profile-head-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ppc-profile-info-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 14px;
                    font-size: 17px;
                }
                .ppc-profile-info-icon.teal {
                    background: #dff5f2;
                    color: #3d9189;
                }
                .ppc-profile-info-icon.green {
                    background: #e6f7ea;
                    color: #1f7a3f;
                }
                .ppc-profile-info-icon.purple {
                    background: #f2e7ff;
                    color: #b369db;
                }
                .ppc-profile-info-icon.gold {
                    background: #fff4d9;
                    color: #f59e0b;
                }
                .ppc-profile-info-label {
                    color: #94a3b8;
                    font-size: 12px;
                    font-weight: 800;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    display: block;
                    margin-bottom: 4px;
                }
                .ppc-profile-info-value {
                    color: #0f172a;
                    font-size: 14px;
                    font-weight: 600;
                    word-break: break-word;
                }
                .ppc-profile-stack {
                    display: grid;
                    gap: 16px;
                }
                .ppc-profile-detail-card {
                    padding: 18px 20px 22px;
                }
                .ppc-profile-detail-head {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 18px;
                }
                .ppc-profile-head-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 14px;
                    font-size: 18px;
                }
                .ppc-profile-head-icon.security {
                    background: #e7f4f5;
                    color: #4d978f;
                }
                .ppc-profile-head-icon.tips {
                    background: #fff3da;
                    color: #f59e0b;
                }
                .ppc-profile-detail-title {
                    color: #a1a1aa;
                    font-size: 12px;
                    font-weight: 800;
                    letter-spacing: 1.1px;
                    text-transform: uppercase;
                }
                .ppc-profile-detail-rows {
                    margin-top: 6px;
                }
                .ppc-profile-detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 14px;
                    padding: 14px 0;
                    border-bottom: 1px solid #edf2f7;
                }
                .ppc-profile-detail-row:last-child {
                    border-bottom: none;
                }
                .ppc-profile-detail-key {
                    color: #64748b;
                    font-size: 15px;
                    font-weight: 600;
                }
                .ppc-profile-detail-value {
                    color: #0f172a;
                    font-size: 15px;
                    font-weight: 600;
                    text-align: right;
                }
                .ppc-profile-detail-value.status {
                    color: #1f7a3f;
                }
                .ppc-profile-tips-list {
                    display: grid;
                    gap: 12px;
                }
                .ppc-profile-tip {
                    display: grid;
                    grid-template-columns: 44px minmax(0, 1fr);
                    gap: 14px;
                    align-items: center;
                    background: #f8fafc;
                    border-radius: 18px;
                    padding: 12px 14px;
                    border: 1px solid #eef2f7;
                }
                .ppc-profile-tip-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 14px;
                    font-size: 18px;
                }
                .ppc-profile-tip-icon.teal {
                    background: #e1f4f2;
                    color: #3d9189;
                }
                .ppc-profile-tip-icon.green {
                    background: #e7f8eb;
                    color: #1f7a3f;
                }
                .ppc-profile-tip-icon.blue {
                    background: #e8f2ff;
                    color: #3b82f6;
                }
                .ppc-profile-tip-icon.gold {
                    background: #fff1da;
                    color: #f59e0b;
                }
                .ppc-profile-tip-text {
                    color: #334155;
                    font-size: 15px;
                    font-weight: 600;
                    line-height: 1.45;
                }
                .ppc-profile-actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    margin-top: 12px;
                }
                .ppc-profile-action-btn {
                    border: none;
                    border-radius: 14px;
                    padding: 12px 18px;
                    color: #fff;
                    font-size: 15px;
                    font-weight: 800;
                    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .ppc-profile-action-btn:hover {
                    transform: translateY(-1px);
                }
                .ppc-profile-action-btn.report {
                    background: linear-gradient(135deg, #4d978f, #4b8bbf);
                }
                .ppc-profile-action-btn.dashboard {
                    background: linear-gradient(135deg, #1f7a3f, #0f9d58);
                }
                .ppc-profile-action-btn.photo {
                    background: linear-gradient(135deg, #bb6bd9, #d67bea);
                }
                .ppc-profile-photo-input {
                    display: none;
                }
                .ppc-profile-skeleton-grid {
                    display: grid;
                    gap: 12px;
                    margin-top: 18px;
                }
                @media (max-width: 1199px) {
                    .ppc-profile-page .page-content {
                        padding-bottom: 120px;
                    }
                    .ppc-profile-shell {
                        grid-template-columns: 1fr;
                    }
                }
                @media (max-width: 575px) {
                    .ppc-profile-page .page-content {
                        padding: 0.75rem 0.75rem 120px;
                    }
                    .ppc-profile-body {
                        padding: 72px 18px 22px;
                    }
                    .ppc-profile-avatar-row {
                        top: 60px;
                        left: 18px;
                        right: 18px;
                        align-items: center;
                    }
                    .ppc-profile-name,
                    .ppc-profile-subtitle,
                    .ppc-profile-section-label {
                        text-align: center;
                    }
                    .ppc-profile-detail-row {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .ppc-profile-detail-value {
                        text-align: left;
                    }
                    .ppc-profile-action-btn {
                        width: 100%;
                    }
                }
            `}</style>

            <ContentWrapper>
                <div className="ppc-profile-shell">
                    <div className="ppc-profile-card ppc-profile-panel">
                        <div className="ppc-profile-hero" />
                        <div className="ppc-profile-body">
                            <div className="ppc-profile-avatar-row">
                                {isLoading ? (
                                    <Skeleton circle width={112} height={112} />
                                ) : (
                                    <img
                                        src={profileImage}
                                        alt={ppc?.ppc_name || 'PPC User'}
                                        className="ppc-profile-avatar"
                                    />
                                )}
                                <div className="ppc-profile-status">
                                    <FaCheckCircle />
                                    {ppc?.ppc_status || 'Active'}
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="ppc-profile-skeleton-grid">
                                    <Skeleton height={28} width="62%" />
                                    <Skeleton height={20} width="40%" />
                                </div>
                            ) : (
                                <>
                                    <div className="ppc-profile-name">{ppc?.ppc_name || 'PPC User'}</div>
                                    <div className="ppc-profile-subtitle">
                                        PPC Team {ppc?.ppc_id ? `- ID #${ppc.ppc_id}` : ''}
                                    </div>
                                </>
                            )}

                            <div className="ppc-profile-divider" />
                            <div className="ppc-profile-section-label">Contact Information</div>

                            <div className="ppc-profile-info-list">
                                {isLoading
                                    ? Array.from({ length: 4 }).map((_, index) => (
                                        <div className="ppc-profile-info-item" key={index}>
                                            <Skeleton width={40} height={40} borderRadius={14} />
                                            <div>
                                                <Skeleton height={12} width={110} />
                                                <Skeleton height={18} width={180} style={{ marginTop: 8 }} />
                                            </div>
                                        </div>
                                    ))
                                    : profileRows.map((item) => (
                                        <div className="ppc-profile-info-item" key={item.label}>
                                            <div className={`ppc-profile-info-icon ${item.accent}`}>{item.icon}</div>
                                            <div>
                                                <span className="ppc-profile-info-label">{item.label}</span>
                                                <span className="ppc-profile-info-value">{item.value}</span>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>

                    <div className="ppc-profile-stack">
                        <div className="ppc-profile-card ppc-profile-detail-card">
                            <div className="ppc-profile-detail-head">
                                <div className="ppc-profile-head-icon security">
                                    <FaShieldAlt />
                                </div>
                                <div className="ppc-profile-detail-title">Account Details</div>
                            </div>

                            <div className="ppc-profile-detail-rows">
                                {isLoading
                                    ? Array.from({ length: 6 }).map((_, index) => (
                                        <div className="ppc-profile-detail-row" key={index}>
                                            <Skeleton height={16} width={120} />
                                            <Skeleton height={16} width={90} />
                                        </div>
                                    ))
                                    : accountRows.map((row) => (
                                        <div className="ppc-profile-detail-row" key={row.label}>
                                            <div className="ppc-profile-detail-key">{row.label}</div>
                                            <div className={`ppc-profile-detail-value ${row.label === 'Account Status' ? 'status' : ''}`}>
                                                {row.value}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        <div className="ppc-profile-card ppc-profile-detail-card">
                            <div className="ppc-profile-detail-head">
                                <div className="ppc-profile-head-icon tips">
                                    <HiSparkles />
                                </div>
                                <div className="ppc-profile-detail-title">Tips & Reminders</div>
                            </div>

                            <div className="ppc-profile-tips-list">
                                {tipItems.map((tip) => (
                                    <div className="ppc-profile-tip" key={tip.title}>
                                        <div className={`ppc-profile-tip-icon ${tip.accent}`}>{tip.icon}</div>
                                        <div className="ppc-profile-tip-text">{tip.title}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ppc-profile-card ppc-profile-detail-card">
                            <div className="ppc-profile-mini-label">Quick Actions</div>
                            <div className="ppc-profile-actions">
                                <button
                                    type="button"
                                    className="ppc-profile-action-btn report"
                                    onClick={() => navigate('/hourly-today-report')}
                                >
                                    <FiActivity style={{ marginRight: 8 }} />
                                    Today Report
                                </button>
                                <button
                                    type="button"
                                    className="ppc-profile-action-btn dashboard"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <MdSpaceDashboard style={{ marginRight: 8 }} />
                                    Dashboard
                                </button>
                                <button
                                    type="button"
                                    className="ppc-profile-action-btn photo"
                                    disabled={isUploading}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <MdCloudUpload style={{ marginRight: 8 }} />
                                    {isUploading ? 'Uploading...' : 'Change Photo'}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    className="ppc-profile-photo-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfileUpload}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default MyProfile;
